import { useId } from 'react';

interface TransitSafeLogoProps {
  size?: number;
  className?: string;
}

/**
 * TransitSafe brand mark — abstract motion + speed.
 *
 * Three swept chevrons stacked in a forward-lean, each offset and fading
 * in toward the lead chevron. Reads as motion/velocity/compliance-across-
 * modes without committing to any one vehicle (aviation, maritime, auto,
 * rail, aerospace, cold-chain freight — TransitSafe serves them all, so
 * the mark must not anchor to a single mode).
 *
 * The gradient matches migration 0021's TransitSafe tenant primary colors
 * (#0ea5e9 → #0284c7, sky-500 to sky-600). `useId()` keeps the gradient
 * ID unique per instance so multiple logos on the same page don't cross
 * their gradient definitions.
 *
 * Scales cleanly from 20px (header compact) to 64px (hero/OG image).
 */
export function TransitSafeLogo({ size = 32, className = '' }: TransitSafeLogoProps) {
  const gradientId = useId();
  const trailGradientId = useId();

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      className={className}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#38bdf8" />  {/* sky-400 */}
          <stop offset="55%"  stopColor="#0ea5e9" />  {/* sky-500 — TransitSafe primary */}
          <stop offset="100%" stopColor="#0284c7" />  {/* sky-600 — TransitSafe accent */}
        </linearGradient>
        <linearGradient id={trailGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#0ea5e9" stopOpacity="0" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Trail — thin horizontal stroke fading in from the left, implies
          the chevrons are sweeping forward through space. */}
      <line
        x1="4"  y1="32"
        x2="18" y2="32"
        stroke={`url(#${trailGradientId})`}
        strokeWidth="2"
        strokeLinecap="round"
      />

      {/* Three chevrons, stacked with a forward tilt. Each is a ">"
          formed by two line segments meeting at the apex. Opacity
          increases toward the lead chevron — reads as motion blur
          without being literal. */}
      {/* Trailing chevron */}
      <path
        d="M 20 22 L 30 32 L 20 42"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.35"
      />
      {/* Middle chevron */}
      <path
        d="M 30 22 L 40 32 L 30 42"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.7"
      />
      {/* Lead chevron */}
      <path
        d="M 40 22 L 50 32 L 40 42"
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
